import React, {useState} from 'react';
import {
  View,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import {Text, TextInput} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {onboardingColors} from '../../theme/onboardingColors';
import {useRegistrationStore} from '../../store/useRegistrationStore';
import {authApi} from '../../services/api/authApi';
import StepIndicator from '../../components/StepIndicator';
import CountryPickerModal from '../../components/CountryPickerModal';

const getFlag = code =>
  String.fromCodePoint(
    ...code
      .toUpperCase()
      .split('')
      .map(c => 127397 + c.charCodeAt()),
  );

const RegisterStep2Screen = ({navigation}) => {
  const {t} = useTranslation();
  const {phone, countryCode, countryIso, email, setField} = useRegistrationStore();
  const [localPhone, setLocalPhone] = useState(phone);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pickerVisible, setPickerVisible] = useState(false);

  const handleNext = async () => {
    const trimmed = localPhone.trim();
    if (!trimmed || trimmed.length < 7) {
      setError(t('validation.phone'));
      return;
    }
    setField('phone', trimmed);
    setLoading(true);
    setError('');
    try {
      await authApi.requestOtp({countryCode, phone: trimmed, purpose: 'REGISTRATION', email});
      navigation.navigate('RegisterStep3');
    } catch (err) {
      setError(err.response?.data?.error?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCountrySelect = country => {
    setField('countryCode', country.dialCode);
    setField('countryIso', country.code);
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled">
        <StepIndicator currentStep={2} />

        <Text style={styles.title}>
          {t('registration.enterPhoneNumber')}
        </Text>
        <Text style={styles.subtitle}>{t('auth.phone')}</Text>

        <View style={styles.phoneRow}>
          <TouchableOpacity
            style={styles.countryButton}
            onPress={() => setPickerVisible(true)}>
            <Text style={styles.flag}>{getFlag(countryIso)}</Text>
            <Text style={styles.dialCode}>{countryCode}</Text>
            <Icon
              name="chevron-down"
              size={18}
              color={onboardingColors.textSecondary}
            />
          </TouchableOpacity>

          <TextInput
            value={localPhone}
            onChangeText={setLocalPhone}
            keyboardType="phone-pad"
            placeholder="Phone number"
            placeholderTextColor={onboardingColors.textSecondary}
            mode="outlined"
            style={styles.phoneInput}
            outlineColor={onboardingColors.dot}
            activeOutlineColor={onboardingColors.primary}
          />
        </View>

        {error ? <Text style={styles.errorText}>{error}</Text> : null}

        <TouchableOpacity
          style={[styles.nextButton, loading && styles.nextButtonDisabled]}
          onPress={handleNext}
          disabled={loading}>
          <Text style={styles.nextButtonText}>
            {loading ? t('common.loading') : t('common.next')}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.loginLink}
          onPress={() => navigation.navigate('Login')}>
          <Text style={styles.loginLinkText}>
            {t('auth.alreadyHaveAccount')}{' '}
            <Text style={styles.loginLinkBold}>{t('auth.login')}</Text>
          </Text>
        </TouchableOpacity>
      </ScrollView>

      <CountryPickerModal
        visible={pickerVisible}
        onClose={() => setPickerVisible(false)}
        onSelect={handleCountrySelect}
      />
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: onboardingColors.background,
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: 32,
    paddingTop: 60,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: onboardingColors.text,
    textAlign: 'center',
    marginTop: 16,
  },
  subtitle: {
    fontSize: 14,
    color: onboardingColors.textSecondary,
    textAlign: 'center',
    marginTop: 4,
    marginBottom: 32,
  },
  phoneRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  countryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: onboardingColors.white,
    borderWidth: 1,
    borderColor: onboardingColors.dot,
    borderRadius: 4,
    paddingHorizontal: 12,
    height: 56,
    marginRight: 8,
  },
  flag: {
    fontSize: 20,
    marginRight: 4,
  },
  dialCode: {
    fontSize: 16,
    color: onboardingColors.text,
    marginRight: 2,
  },
  phoneInput: {
    flex: 1,
    backgroundColor: onboardingColors.white,
  },
  errorText: {
    color: '#D32F2F',
    fontSize: 13,
    marginBottom: 16,
    marginLeft: 4,
  },
  nextButton: {
    backgroundColor: onboardingColors.primary,
    borderRadius: 28,
    paddingVertical: 14,
    alignItems: 'center',
  },
  nextButtonDisabled: {
    opacity: 0.6,
  },
  nextButtonText: {
    color: onboardingColors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  loginLink: {
    marginTop: 24,
    alignItems: 'center',
  },
  loginLinkText: {
    fontSize: 14,
    color: onboardingColors.textSecondary,
  },
  loginLinkBold: {
    color: onboardingColors.primary,
    fontWeight: '600',
  },
});

export default RegisterStep2Screen;
